import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { invalidateProductLists } from './useProductMutation';

/**
 * ID60 상품 복사. BE `POST /admin/v1/products/{productId}/copy` 가 deep-clone
 * (translations/options/variants/prices/thumbnail/detailImages/notice/policy/
 * artist/category) 후 `[복사본] ` prefix + status=DRAFT + displayed=false 로
 * 새 상품을 만들고 ProductDetailView 를 반환한다. FE 는 새 productId 로 수정
 * 화면으로 라우팅만 한다 (prefix/상태 세팅은 모두 BE 권위).
 *
 * 복사 endpoint 는 generated OpenAPI 스펙에 아직 없어 `request(... as never)`
 * escape-hatch 로 호출한다 (MigratedPasswordChangeForm 선례와 동일 관례).
 * Idempotency-Key 는 writeAllowlist 패턴 매칭으로 미들웨어가 자동 부착한다.
 */
export interface CopiedProduct {
  productId: string;
  name?: string | null;
}

const COPY_PATH = '/admin/v1/products/{productId}/copy';

export const useCopyProductMutation = () => {
  const qc = useQueryClient();
  return useMutation<CopiedProduct, Error, string>({
    mutationFn: async (productId) => {
      const [data] = await apiClient.request(
        'post',
        COPY_PATH as never,
        { params: { path: { productId } } } as never,
      );
      const detail = data as { productId?: string; name?: string | null } | null;
      if (!detail?.productId) {
        throw new Error('copy response missing productId');
      }
      return { productId: detail.productId, name: detail.name ?? null };
    },
    // 복사본도 원본 카테고리로 매핑 행이 생긴다 — 상품 저장과 같은 무효화.
    onSuccess: () => invalidateProductLists(qc),
  });
};
